import React, { useState } from "react";
import { motion } from "framer-motion";
import { Database, ShieldAlert, Cpu, Lock, Network, Users, ArrowRight } from "lucide-react";

export default function SaasMultiTenant() {
  const [activeTenant, setActiveTenant] = useState("school");

  const tenants = {
    school: {
      name: "ABC International School",
      type: "School Tenant",
      color: "from-violet-500 to-indigo-600",
      textColor: "text-violet-500",
      description: "Isolated environment for K-12 management. Custom syllabus, attendance modules, and parental access portals.",
      stats: { students: "1,200", teachers: "85", security: "AES-256 isolated" }
    },
    college: {
      name: "XYZ Institute of Technology",
      type: "College Tenant",
      color: "from-blue-500 to-indigo-600",
      textColor: "text-blue-500",
      description: "University-grade setup with placement modules, credit systems, complex exams, and multi-department controls.",
      stats: { students: "8,500", teachers: "420", security: "SOC2 isolated" }
    },
    coaching: {
      name: "PQR Coaching Academy",
      type: "Coaching Centre Tenant",
      color: "from-cyan-500 to-blue-600",
      textColor: "text-cyan-500",
      description: "High-frequency preparation environment. Optimized for MCQ test-series, speed evaluations, and leaderboard sync.",
      stats: { students: "15,000", teachers: "120", security: "Isolated DB Instance" }
    }
  };

  return (
    <section id="multi-tenant" className="py-24 bg-slate-50 dark:bg-slate-900 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-900/50 rounded-full text-blue-600 dark:text-blue-400 text-sm font-bold mb-4">
            <Network size={16} />
            <span>Architecture & Security</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 dark:text-white tracking-tight">
            One Platform. Unlimited Institutes.
          </h2>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-300 font-medium">
            Our enterprise-grade multi-tenant architecture keeps your data isolated and secure, while sharing high-performance infrastructure to minimize latency and cost.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-16 items-center">
          
          {/* Left Column: Interactive Diagram */}
          <div className="lg:col-span-7 flex flex-col items-center">
            
            {/* Diagram Container */}
            <div className="relative w-full max-w-md aspect-square bg-white dark:bg-slate-950 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 p-8 shadow-xl flex flex-col justify-between overflow-hidden">
              {/* Background glows */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-gradient-to-r from-violet-600/10 to-blue-500/10 rounded-full blur-2xl" />

              {/* Step 1: Platform Core (Global Server) */}
              <div className="flex flex-col items-center relative z-10">
                <motion.div
                  animate={{ y: [0, -4, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="w-20 h-20 rounded-2xl bg-gradient-to-r from-violet-600 via-blue-600 to-cyan-500 p-0.5 shadow-2xl flex items-center justify-center"
                >
                  <div className="w-full h-full bg-slate-900 rounded-[14px] flex flex-col items-center justify-center text-white">
                    <Database size={24} className="text-violet-400 animate-pulse" />
                    <span className="text-[10px] font-black tracking-wider uppercase mt-1">CORE INFRA</span>
                  </div>
                </motion.div>
                {/* Horizontal flow line downward */}
                <div className="w-0.5 h-10 bg-gradient-to-b from-blue-500 to-slate-200 dark:to-slate-800" />
              </div>

              {/* Step 2: Tenant Routing Nodes */}
              <div className="grid grid-cols-3 gap-6 relative z-10">
                
                {/* School Node */}
                <button
                  onClick={() => setActiveTenant("school")}
                  className={`flex flex-col items-center p-3 rounded-2xl transition-all border ${
                    activeTenant === "school"
                      ? "bg-violet-50/50 dark:bg-violet-950/20 border-violet-500/50 shadow-md"
                      : "border-transparent hover:border-slate-200 dark:hover:border-slate-800"
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 text-white flex items-center justify-center shadow-lg transition-transform ${activeTenant === "school" ? "scale-110" : ""}`}>
                    <Cpu size={20} />
                  </div>
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-200 mt-2">School (ABC)</span>
                </button>

                {/* College Node */}
                <button
                  onClick={() => setActiveTenant("college")}
                  className={`flex flex-col items-center p-3 rounded-2xl transition-all border ${
                    activeTenant === "college"
                      ? "bg-blue-50/50 dark:bg-blue-950/20 border-blue-500/50 shadow-md"
                      : "border-transparent hover:border-slate-200 dark:hover:border-slate-800"
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center shadow-lg transition-transform ${activeTenant === "college" ? "scale-110" : ""}`}>
                    <Cpu size={20} />
                  </div>
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-200 mt-2">College (XYZ)</span>
                </button>

                {/* Coaching Node */}
                <button
                  onClick={() => setActiveTenant("coaching")}
                  className={`flex flex-col items-center p-3 rounded-2xl transition-all border ${
                    activeTenant === "coaching"
                      ? "bg-cyan-50/50 dark:bg-cyan-950/20 border-cyan-500/50 shadow-md"
                      : "border-transparent hover:border-slate-200 dark:hover:border-slate-800"
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center shadow-lg transition-transform ${activeTenant === "coaching" ? "scale-110" : ""}`}>
                    <Cpu size={20} />
                  </div>
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-200 mt-2">Coaching (PQR)</span>
                </button>

              </div>

              {/* Step 3: Isolated Data Clusters (Visually showing isolated segments) */}
              <div className="flex justify-between items-center relative z-10 border-t border-slate-100 dark:border-slate-800 pt-6">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-violet-500" />
                  <span className="text-[10px] font-bold text-slate-500">DB Isolated</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                  <span className="text-[10px] font-bold text-slate-500">Separate Media CDN</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-cyan-500" />
                  <span className="text-[10px] font-bold text-slate-500">Encrypted Keys</span>
                </div>
              </div>

            </div>

            {/* Selector helper */}
            <p className="text-xs text-slate-400 mt-4 font-bold">
              * Click any tenant node above to inspect its architecture isolation.
            </p>
          </div>

          {/* Right Column: Architectural explanations */}
          <div className="lg:col-span-5 space-y-8">
            <div className="bg-white dark:bg-slate-950 p-8 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm">
              <div className={`inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r ${tenants[activeTenant].color} text-white rounded-full text-xs font-bold mb-4`}>
                <span>{tenants[activeTenant].type}</span>
              </div>
              <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-3">
                {tenants[activeTenant].name}
              </h3>
              <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed mb-6 font-medium">
                {tenants[activeTenant].description}
              </p>

              {/* Data Table */}
              <div className="grid grid-cols-3 gap-4 border-t border-slate-100 dark:border-slate-800 pt-6">
                <div>
                  <p className="text-[10px] text-slate-400 uppercase font-black">Capacity</p>
                  <p className="text-sm font-black text-slate-800 dark:text-white mt-0.5">{tenants[activeTenant].stats.students} Users</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase font-black">Staff</p>
                  <p className="text-sm font-black text-slate-800 dark:text-white mt-0.5">{tenants[activeTenant].stats.teachers} Admins</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase font-black">Security</p>
                  <p className="text-sm font-black text-emerald-600 dark:text-emerald-400 mt-0.5">{tenants[activeTenant].stats.security}</p>
                </div>
              </div>
            </div>

            {/* Architecture key pillars */}
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-violet-100 dark:bg-violet-950 text-violet-600 dark:text-violet-400 rounded-xl flex items-center justify-center shrink-0">
                  <Lock size={20} />
                </div>
                <div>
                  <h4 className="text-base font-bold text-slate-950 dark:text-white">Complete Tenant Isolation</h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                    Student accounts, course contents, custom templates, and exam scripts are strictly separated at the database level.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-950 text-blue-600 dark:text-blue-400 rounded-xl flex items-center justify-center shrink-0">
                  <Users size={20} />
                </div>
                <div>
                  <h4 className="text-base font-bold text-slate-950 dark:text-white">Role-Based Access Control (RBAC)</h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                    Granular permissions mapping for Institute Owners, Teachers, Evaluators, and Parents.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-10 h-10 bg-cyan-100 dark:bg-cyan-950 text-cyan-600 dark:text-cyan-400 rounded-xl flex items-center justify-center shrink-0">
                  <ShieldAlert size={20} />
                </div>
                <div>
                  <h4 className="text-base font-bold text-slate-950 dark:text-white">Enterprise security standards</h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                    TLS 1.3 encryption transit, AES-256 rest, SOC2 compliant hosting and detailed audit logs.
                  </p>
                </div>
              </div>
            </div>

          </div>

        </div>

      </div>
    </section>
  );
}
